import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Service, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { firstValueFrom, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IData } from '../../models/api/data';
import { IError } from '../../models/error/error';
import { IUserDevice } from '../../models/user/user-device';
import { ELocalStorage } from 'app/core/models/authentication/authentication';
import { HashHelper } from 'app/core/helpers/hash.helper';

@Service()
export class UserDeviceService {
    private readonly router = inject(Router);
    private readonly httpClient = inject(HttpClient);
    private readonly deviceService = inject(DeviceDetectorService);
    private readonly translateService = inject(TranslateService);

    public readonly userDeviceId = signal<string | null>(null);

    public register(token?: string | null): Observable<IUserDevice> {
        let name = localStorage.getItem(ELocalStorage.K_DEVICE_ID);

        if (!name) {
            name = HashHelper.uuidv4();
            localStorage.setItem(ELocalStorage.K_DEVICE_ID, name);
        }

        const deviceInfo = this.deviceService.deviceInfo();
        const parameters = {
            name,
            token: token ?? '',
            model: deviceInfo.browser,
            appVersion: environment.applicationVersion,
            osVersion: deviceInfo.os_version !== 'unknown' ? deviceInfo.os_version : deviceInfo.browser_version,
            osType: 'browser',
            language: this.translateService.getBrowserLang(),
            environment: environment.type === 'local' ? 'develop' : environment.type
        };

        const baseUrl = ['clinic', 'admin', 'user', 'device'];
        const url = this.router.createUrlTree(baseUrl);
        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post(parsedUrl, parameters, { responseType: 'json' }).pipe(
            map((res: IData<IUserDevice>) => {
                this.userDeviceId.set(res.data.id);
                return res.data;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error?.message}`);
                }
                const errorApi: IError = error.error?.error;
                return throwError(() => errorApi);
            })
        );
    }

    public async remove(): Promise<null> {
        const userDeviceId = this.userDeviceId();
        if (!userDeviceId) {
            return;
        }

        this.userDeviceId.set(null);

        const baseUrl = ['clinic', 'admin', 'user', 'device', userDeviceId];
        const url = this.router.createUrlTree(baseUrl);
        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return firstValueFrom(this.httpClient.delete<null>(parsedUrl, { responseType: 'json' }));
    }
}
